import React, { useEffect, useState, useMemo } from "react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Search, Download, Trash2, MessageSquare, X, ChevronRight } from "lucide-react";

const STATUSES = [
  { v: "new", l: "Novo", c: "bg-accent-yellow text-black" },
  { v: "contacted", l: "Contatado", c: "bg-blue-100 text-blue-800" },
  { v: "qualified", l: "Qualificado", c: "bg-accent-green text-black" },
  { v: "won", l: "Convertido", c: "bg-emerald-600 text-white" },
  { v: "lost", l: "Perdido", c: "bg-neutral-300 text-neutral-700" },
];

function fmt(d) { try { return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch { return d; } }
function statusMeta(v) { return STATUSES.find((s) => s.v === v) || { l: v || "Novo", c: "bg-neutral-200 text-neutral-700" }; }

export default function ConciergeLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/concierge-logs?${params.toString()}`);
      setLogs(data || []);
      const s = await api.get("/admin/concierge-logs/stats");
      setStats(s.data);
    } catch (e) {
      toast.error("Erro ao carregar leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/concierge-logs/${id}`, { status });
      toast.success("Status atualizado.");
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Falha ao atualizar."); }
  };

  const saveNotes = async (id, notes, contact) => {
    try {
      await api.patch(`/admin/concierge-logs/${id}`, { notes, ...contact });
      toast.success("Salvo.");
      load();
    } catch { toast.error("Erro ao salvar."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este lead permanentemente?")) return;
    try {
      await api.delete(`/admin/concierge-logs/${id}`);
      toast.success("Excluído.");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Falha."); }
  };

  const exportCsv = () => {
    window.open(`${API_BASE}/admin/concierge-logs.csv`, "_blank");
  };

  const filtered = useMemo(() => {
    if (!q) return logs;
    const k = q.toLowerCase();
    return logs.filter((l) => `${l.challenge} ${l.location} ${l.profile} ${l.contact_email} ${l.contact_name} ${l.phase}`.toLowerCase().includes(k));
  }, [logs, q]);

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl flex items-center gap-2"><MessageSquare className="w-7 h-7 text-accent-orange" /> Concierge — Leads</h1>
          <p className="text-sm text-neutral-500 mt-1">Cada conversa com o Concierge IA fica registrada aqui.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="concierge-export-csv" className="text-sm border border-black/15 px-4 py-2 rounded-full hover:bg-black hover:text-white transition flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <div className="border border-black/10 p-3 rounded">
            <div className="text-xs text-neutral-500">Total</div>
            <div className="font-display text-3xl">{stats.total || 0}</div>
          </div>
          {STATUSES.map((s) => (
            <div key={s.v} className="border border-black/10 p-3 rounded">
              <div className={`inline-block text-[10px] px-2 py-0.5 rounded ${s.c}`}>{s.l}</div>
              <div className="font-display text-3xl mt-1">{stats.by_status?.[s.v] || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setStatusFilter("all")} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === "all" ? "bg-black text-white border-black" : "border-black/15"}`} data-testid="filter-all">Todos</button>
        {STATUSES.map((s) => (
          <button key={s.v} onClick={() => setStatusFilter(s.v)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s.v ? "bg-black text-white border-black" : "border-black/15"}`} data-testid={`filter-${s.v}`}>{s.l}</button>
        ))}
        <div className="flex items-center gap-2 ml-auto border border-black/15 rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por desafio, perfil, email..." className="bg-transparent outline-none text-xs w-64" data-testid="concierge-search" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-${selected ? 2 : 3}`}>
          {loading ? (
            <div className="text-sm text-neutral-500 py-10 text-center">Carregando…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-black/15 p-10 text-center text-sm text-neutral-500 rounded">
              Nenhuma conversa registrada. Quando alguém usar o Concierge no site, aparecerá aqui.
            </div>
          ) : (
            <div className="border border-black/10 rounded overflow-hidden">
              <table className="w-full text-sm" data-testid="concierge-logs-table">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-600">
                  <tr>
                    <th className="text-left p-3">Quando</th>
                    <th className="text-left p-3">Perfil</th>
                    <th className="text-left p-3">Fase</th>
                    <th className="text-left p-3">Desafio</th>
                    <th className="text-left p-3">Contato</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className={`border-t border-black/5 hover:bg-black/[0.02] cursor-pointer ${selected?.id === l.id ? "bg-accent-yellow/20" : ""}`} onClick={() => setSelected(l)} data-testid={`concierge-row-${l.id}`}>
                      <td className="p-3 text-xs text-neutral-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                      <td className="p-3">{l.profile}<div className="text-[10px] text-neutral-400">{l.development_type}</div></td>
                      <td className="p-3">{l.phase}</td>
                      <td className="p-3 max-w-xs truncate" title={l.challenge}>{l.challenge}</td>
                      <td className="p-3 text-xs">{l.contact_email || l.contact_name || <span className="text-neutral-400">—</span>}</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded ${statusMeta(l.status).c}`}>{statusMeta(l.status).l}</span></td>
                      <td className="p-3 text-neutral-400"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <LeadDetail key={selected.id} lead={selected} onClose={() => setSelected(null)} onSetStatus={setStatus} onSaveNotes={saveNotes} onDelete={remove} />
        )}
      </div>
    </div>
  );
}

function LeadDetail({ lead, onClose, onSetStatus, onSaveNotes, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [contact, setContact] = useState({
    contact_name: lead.contact_name || "",
    contact_email: lead.contact_email || "",
    contact_phone: lead.contact_phone || "",
  });

  useEffect(() => {
    setNotes(lead.notes || "");
    setContact({
      contact_name: lead.contact_name || "",
      contact_email: lead.contact_email || "",
      contact_phone: lead.contact_phone || "",
    });
  }, [lead.id]); // eslint-disable-line

  return (
    <aside className="lg:col-span-1 border border-black/10 rounded p-5 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="concierge-detail">
      <div className="flex justify-between items-center mb-4">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Detalhe do lead</div>
        <button onClick={onClose} data-testid="detail-close"><X className="w-4 h-4" /></button>
      </div>

      <div className="space-y-3 text-sm">
        <div><div className="text-[10px] text-neutral-400 uppercase">Quando</div>{fmt(lead.created_at)}</div>
        <div className="grid grid-cols-2 gap-2">
          <div><div className="text-[10px] text-neutral-400 uppercase">Perfil</div>{lead.profile}</div>
          <div><div className="text-[10px] text-neutral-400 uppercase">Tipo</div>{lead.development_type}</div>
          <div><div className="text-[10px] text-neutral-400 uppercase">Fase</div>{lead.phase}</div>
          <div><div className="text-[10px] text-neutral-400 uppercase">Localização</div>{lead.location || "—"}</div>
        </div>
        <div><div className="text-[10px] text-neutral-400 uppercase">Desafio relatado</div><div className="bg-black/5 p-2 rounded text-xs">{lead.challenge}</div></div>

        <div>
          <div className="text-[10px] text-neutral-400 uppercase mb-1">Serviços recomendados</div>
          <ul className="text-xs space-y-1">{(lead.services || []).map((s) => <li key={s} className="border-l-2 border-accent-green pl-2">{s}</li>)}</ul>
        </div>

        {lead.reasoning && <div><div className="text-[10px] text-neutral-400 uppercase">IA — Raciocínio</div><div className="text-xs text-neutral-600 italic">{lead.reasoning}</div></div>}

        <div className="border-t border-black/10 pt-3">
          <div className="text-[10px] text-neutral-400 uppercase mb-2">Status</div>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button key={s.v} onClick={() => onSetStatus(lead.id, s.v)} data-testid={`set-status-${s.v}`} className={`text-[10px] px-2.5 py-1 rounded-full transition ${lead.status === s.v ? s.c + " font-semibold" : "border border-black/15 hover:bg-black/5"}`}>{s.l}</button>
            ))}
          </div>
        </div>

        <div className="border-t border-black/10 pt-3 space-y-2">
          <div className="text-[10px] text-neutral-400 uppercase">Dados de contato</div>
          <input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} placeholder="Nome" className="w-full border border-black/15 px-2 py-1.5 rounded text-xs" data-testid="lead-name" />
          <input value={contact.contact_email} onChange={(e) => setContact({ ...contact, contact_email: e.target.value })} placeholder="E-mail" className="w-full border border-black/15 px-2 py-1.5 rounded text-xs" data-testid="lead-email" />
          <input value={contact.contact_phone} onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })} placeholder="Telefone" className="w-full border border-black/15 px-2 py-1.5 rounded text-xs" data-testid="lead-phone" />
        </div>

        <div className="border-t border-black/10 pt-3">
          <div className="text-[10px] text-neutral-400 uppercase mb-1">Notas internas</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full border border-black/15 px-2 py-1.5 rounded text-xs" placeholder="Próximos passos, observações..." data-testid="lead-notes" />
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={() => onSaveNotes(lead.id, notes, contact)} className="flex-1 bg-black text-white text-xs px-3 py-2 rounded-full hover:bg-accent-orange" data-testid="lead-save">Salvar</button>
          <button onClick={() => onDelete(lead.id)} className="text-red-500 border border-red-200 px-3 py-2 rounded-full text-xs hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
        </div>

        {(lead.contact_email || lead.contact_phone) && (
          <div className="border-t border-black/10 pt-3 space-y-1.5">
            {lead.contact_email && <a href={`mailto:${lead.contact_email}?subject=Places%20-%20Sua%20conversa%20com%20o%20Concierge`} className="block bg-accent-yellow text-black text-center text-xs font-semibold py-2 rounded-full">Responder por e-mail</a>}
            {lead.contact_phone && <a href={`https://wa.me/${lead.contact_phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="block bg-accent-green text-black text-center text-xs font-semibold py-2 rounded-full">Abrir WhatsApp</a>}
          </div>
        )}
      </div>
    </aside>
  );
}
